import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Mail, Trash2, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface TeamMember {
  id: string;
  email: string;
  role: string;
  status: string;
}

export function TeamSettings() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('organization_id', session.user.id);

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      setError('Failed to fetch team members');
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { error } = await supabase.from('team_invites').insert({
        email: inviteEmail,
        role: inviteRole,
        organization_id: session.user.id
      });

      if (error) throw error;

      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setIsInviting(false);
      await fetchTeamMembers();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      await fetchTeamMembers();
    } catch (error) {
      setError('Failed to remove team member');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-500" />
            Team Settings
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Manage your team members and their permissions
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-500/10 text-green-400 rounded-lg text-sm">
            {success}
          </div>
        )}

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-200">Team Members</h3>
            <button
              onClick={() => setIsInviting(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <UserPlus className="w-4 h-4" />
              Invite Member
            </button>
          </div>

          <div className="divide-y divide-gray-700">
            {members.map((member) => (
              <div key={member.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-gray-200">{member.email}</div>
                    <div className="text-sm text-gray-400 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      {member.role}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveMember(member.id)}
                  className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-gray-700"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Invite Member Modal */}
        {isInviting && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-gray-100">Invite Team Member</h2>
                <button
                  onClick={() => setIsInviting(false)}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleInvite} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Email Address
                  </label>
                  <div className="mt-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="block w-full pl-10 bg-gray-700 border border-gray-600 rounded-lg text-gray-100"
                      placeholder="colleague@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-lg text-gray-100"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsInviting(false)}
                    className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <UserPlus className="w-4 h-4" />
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}